import * as i0 from '@angular/core';
import { Injectable, Pipe, NgModule } from '@angular/core';
import * as i1 from '@helix/platform/shared/api';
import { DataPage, RX_BUNDLE, RX_ADMINISTRATION, RX_APPLICATION } from '@helix/platform/shared/api';
import * as i2 from '@ngx-translate/core';
import { chain, reject, map as map$1, isEmpty, remove, last, set, forEach, find, filter, compact, toLower, startCase, sortBy } from 'lodash';
import { BehaviorSubject, combineLatest, of, forkJoin } from 'rxjs';
import { map, catchError, shareReplay, switchMap, tap } from 'rxjs/operators';
import * as i1$1 from '@angular/common/http';
import * as i2$1 from '@helix/platform/process/api';
import { CommonModule } from '@angular/common';
import moment from 'moment';

class RxRuleDataDictionaryService {
    constructor(rxDataDictionaryUtils, rxGlobalCacheService, translateService) {
        this.rxDataDictionaryUtils = rxDataDictionaryUtils;
        this.rxGlobalCacheService = rxGlobalCacheService;
        this.translateService = translateService;
        this.qualificationDataDictionaryBranch$ = this.rxGlobalCacheService
            .getFunctionDescriptors()
            .pipe(map((functionDescriptors) => chain(this.rxDataDictionaryUtils.getFunctionsDataDictionaryBranch(this.getFunctionDataDictionaryDescriptors(functionDescriptors)))
            .filter({ label: this.ruleQualificationFunctionBranchLabel })
            .head()
            .value()));
        this.functionsDataDictionaryBranch$ = this.rxGlobalCacheService
            .getFunctionDescriptors()
            .pipe(map((functionDescriptors) => reject(this.rxDataDictionaryUtils.getFunctionsDataDictionaryBranch(this.getFunctionDataDictionaryDescriptors(functionDescriptors)), { label: this.ruleQualificationFunctionBranchLabel })), map((functionsDataDictionaryBranch) => ({
            label: this.translateService.instant('com.bmc.arsys.rx.client.common.functions.label'),
            expanded: true,
            children: functionsDataDictionaryBranch
        })));
        this.primaryRecordDefinitionDataDictionaryBranchSubject = new BehaviorSubject(null);
        this.ruleQualificationFunctionBranchLabel = 'Rule qualification';
        this.commonDataDictionary$ = combineLatest([
            this.primaryRecordDefinitionDataDictionaryBranchSubject,
            this.functionsDataDictionaryBranch$
        ]).pipe(map(([primaryRecordDefinitionDataDictionaryBranch, functionsDataDictionaryBranch]) => {
            return [
                primaryRecordDefinitionDataDictionaryBranch,
                {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.common.general-items.label'),
                    children: [
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-date.label'),
                            icon: 'd-icon-dollar',
                            expression: '$DATE$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-date-time.label'),
                            icon: 'd-icon-dollar',
                            expression: '$TIMESTAMP$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-group-ids.label'),
                            icon: 'd-icon-dollar',
                            expression: '$GROUPIDS$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-groups.label'),
                            icon: 'd-icon-dollar',
                            expression: '$GROUPS$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.rule-designer.keywords.current-operation.label'),
                            icon: 'd-icon-dollar',
                            expression: '$OPERATION$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.rule-designer.keywords.current-record.label'),
                            icon: 'd-icon-dollar',
                            expression: '$ENTRY$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.rule-designer.keywords.current-record-definition-name.label'),
                            icon: 'd-icon-dollar',
                            expression: '$SCHEMA$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-roles.label'),
                            icon: 'd-icon-dollar',
                            expression: '$ROLES$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-server-urls.label'),
                            icon: 'd-icon-dollar',
                            expression: '$SERVERURL$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-time.label'),
                            icon: 'd-icon-dollar',
                            expression: '$TIME$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-user.label'),
                            icon: 'd-icon-dollar',
                            expression: '$USER$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-user-locale.label'),
                            icon: 'd-icon-dollar',
                            expression: '$LOCALE$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.current-week-day.label'),
                            icon: 'd-icon-dollar',
                            expression: '$WEEKDAY$'
                        },
                        {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.designer.keywords.new-line.label'),
                            icon: 'd-icon-dollar',
                            expression: '$NEWLINE$'
                        },
                        {
                            label: 'NULL',
                            icon: 'd-icon-dollar',
                            expression: '$NULL$',
                            hidden: true
                        }
                    ]
                },
                functionsDataDictionaryBranch
            ];
        }));
    }
    setPrimaryRecordDefinitionDataDictionaryBranch(dataDictionaryBranch) {
        this.primaryRecordDefinitionDataDictionaryBranchSubject.next(dataDictionaryBranch);
    }
    getFunctionDataDictionaryDescriptors(functionDescriptors) {
        return functionDescriptors.map((functionDescriptor) => ({
            category: functionDescriptor.type,
            description: functionDescriptor.description,
            name: functionDescriptor.displayName,
            parameters: functionDescriptor.inputParams,
            signature: `${functionDescriptor.displayName}(${map$1(functionDescriptor.inputParams, 'name').join(', ')})`
        }));
    }
}
RxRuleDataDictionaryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDataDictionaryService, deps: [{ token: i1.RxDataDictionaryUtils }, { token: i1.RxGlobalCacheService }, { token: i2.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
RxRuleDataDictionaryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDataDictionaryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDataDictionaryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.RxDataDictionaryUtils }, { type: i1.RxGlobalCacheService }, { type: i2.TranslateService }]; } });

class RxSystemEventTypeDataPageService extends DataPage {
    constructor(injector) {
        super(injector, 'com.bmc.arsys.rx.application.rule.datapage.SystemEventTypeDataPageQuery');
        this.injector = injector;
    }
}
RxSystemEventTypeDataPageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxSystemEventTypeDataPageService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
RxSystemEventTypeDataPageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxSystemEventTypeDataPageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxSystemEventTypeDataPageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

class RxRuleDefinitionCacheService {
    constructor(errorHandler, rxSystemEventTypeDataPageService) {
        this.errorHandler = errorHandler;
        this.rxSystemEventTypeDataPageService = rxSystemEventTypeDataPageService;
        this.consumers = new Set();
        this.systemEventTypes = null;
    }
    clearCache() {
        this.systemEventTypes = null;
    }
    getSystemEventTypes() {
        if (!this.systemEventTypes) {
            this.systemEventTypes = this.rxSystemEventTypeDataPageService
                .get({
                params: {
                    pageSize: -1,
                    startIndex: 0
                }
            })
                .pipe(map((response) => response.data), catchError((error) => {
                this.errorHandler.handleError(error);
                return of([]);
            }));
        }
        return this.systemEventTypes;
    }
    registerConsumer(consumerDestroy$) {
        this.consumers.add(consumerDestroy$);
        consumerDestroy$.subscribe(() => {
            this.consumers.delete(consumerDestroy$);
            if (isEmpty(this.consumers)) {
                this.clearCache();
            }
        });
    }
}
RxRuleDefinitionCacheService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDefinitionCacheService, deps: [{ token: i0.ErrorHandler }, { token: RxSystemEventTypeDataPageService }], target: i0.ɵɵFactoryTarget.Injectable });
RxRuleDefinitionCacheService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDefinitionCacheService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDefinitionCacheService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.ErrorHandler }, { type: RxSystemEventTypeDataPageService }]; } });

class RxRuleDefinitionDataPageService extends DataPage {
    constructor(injector) {
        super(injector, 'com.bmc.arsys.rx.application.rule.datapage.RuleDefinitionDataPageQuery');
        this.injector = injector;
    }
}
RxRuleDefinitionDataPageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDefinitionDataPageService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
RxRuleDefinitionDataPageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDefinitionDataPageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDefinitionDataPageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

const specialActionNames = {
    connector: 'rx.RuleConnector'
};
const modelTypes = {
    cancelProcess: 'rx.RuleCancelProcess',
    qualification: 'rx.RuleQualification',
    link: 'rx.RuleLink',
    ruleAction: 'rx.RuleAction',
    ruleActions: 'rx.RuleActions',
    ruleStartProcess: 'rx.RuleStartProcess',
    signalProcess: 'rx.RuleSignalProcess',
    startProcess: 'rx.RuleStartProcess',
    trigger: 'rx.RuleTrigger',
    ...specialActionNames
};
const resourceTypes = {
    cancelProcess: 'com.bmc.arsys.rx.services.rule.domain.CancelProcessInstanceAction',
    correlationResourceType: 'com.bmc.arsys.rx.services.rule.domain.SignalProcessAutomaticCorrelation',
    ruleAction: 'com.bmc.arsys.rx.services.rule.domain.CustomRuleAction',
    signalProcess: 'com.bmc.arsys.rx.services.rule.domain.SignalProcessInstanceAction',
    startProcess: 'com.bmc.arsys.rx.services.rule.domain.StartProcessInstanceAction'
};
const RX_RULE_DEFINITION = {
    displayNames: {
        connector: 'Connector'
    },
    modelTypes,
    resourceTypes,
    specialActionNames,
    resourceTypesByModelType: {
        [modelTypes.cancelProcess]: resourceTypes.cancelProcess,
        [modelTypes.ruleAction]: resourceTypes.ruleAction,
        [modelTypes.signalProcess]: resourceTypes.signalProcess,
        [modelTypes.startProcess]: resourceTypes.startProcess,
        [specialActionNames.connector]: resourceTypes.ruleAction
    },
    timerTypes: {
        interval: 'Interval',
        schedule: 'Schedule'
    }
};

var TriggerEventTimeCriteriaResourceTypes;
(function (TriggerEventTimeCriteriaResourceTypes) {
    TriggerEventTimeCriteriaResourceTypes["Interval"] = "com.bmc.arsys.rx.services.rule.domain.IntervalTime";
    TriggerEventTimeCriteriaResourceTypes["Schedule"] = "com.bmc.arsys.rx.services.rule.domain.ScheduleTime";
})(TriggerEventTimeCriteriaResourceTypes || (TriggerEventTimeCriteriaResourceTypes = {}));
var TriggerEventResourceTypes;
(function (TriggerEventResourceTypes) {
    TriggerEventResourceTypes["Record"] = "com.bmc.arsys.rx.services.rule.domain.RecordTriggerEvent";
    TriggerEventResourceTypes["System"] = "com.bmc.arsys.rx.services.rule.domain.SystemTriggerEvent";
    TriggerEventResourceTypes["Timer"] = "com.bmc.arsys.rx.services.rule.domain.TimerTriggerEvent";
})(TriggerEventResourceTypes || (TriggerEventResourceTypes = {}));

const revertRuleCustomizationResourceType = 'com.bmc.arsys.rx.application.rule.command.RevertRuleDefinitionCommand';
const renameRuleDefinitionCommand = 'com.bmc.arsys.rx.application.rule.command.RenameRuleDefinitionCommand';
class RxRuleDefinitionService {
    constructor(httpClient, rxCommandFactoryService) {
        this.httpClient = httpClient;
        this.renameCommand = rxCommandFactoryService.forResourceType(renameRuleDefinitionCommand);
        this.revertCustomizationCommand = rxCommandFactoryService.forResourceType(revertRuleCustomizationResourceType);
    }
    create(ruleDefinition) {
        return this.httpClient.post(this.getUrl(), ruleDefinition, { observe: 'response' });
    }
    get(ruleDefinitionName, options) {
        return this.httpClient.get(this.getUrl(ruleDefinitionName), options);
    }
    getNew() {
        return of({
            actions: [],
            allowOverlay: false,
            description: '',
            guid: null,
            isEnabled: true,
            lastChangedBy: null,
            lastUpdateTime: null,
            localizableStrings: {},
            name: '',
            overlayDescriptor: null,
            overlayGroupId: null,
            owner: null,
            qualification: null,
            recordDefinitionNames: [],
            scope: RX_BUNDLE.definitionScopeTypes.bundle,
            triggerEvent: {
                eventTypes: [],
                executionOrder: 500,
                guid: null,
                resourceType: TriggerEventResourceTypes.Record
            }
        });
    }
    getNumberOfAvailablePools() {
        return this.httpClient
            .get(`${RX_ADMINISTRATION.systemConfigurationUrl}/rulepool/numberofavailablepools`)
            .pipe(map((systemConfiguration) => systemConfiguration.value));
    }
    rename(oldRuleDefinitionName, newRuleDefinitionName) {
        return this.renameCommand.execute({
            name: oldRuleDefinitionName,
            newName: newRuleDefinitionName
        });
    }
    revertCustomization(ruleDefinitionName) {
        return this.revertCustomizationCommand.execute({ ruleDefinitionName });
    }
    update(ruleDefinition) {
        return this.httpClient.put(this.getUrl(ruleDefinition.name), ruleDefinition);
    }
    getUrl(ruleDefinitionName) {
        return ruleDefinitionName
            ? `/api/rx/application/rule/ruledefinition/${encodeURIComponent(ruleDefinitionName)}`
            : '/api/rx/application/rule/ruledefinition';
    }
}
RxRuleDefinitionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDefinitionService, deps: [{ token: i1$1.HttpClient }, { token: i1.RxCommandFactoryService }], target: i0.ɵɵFactoryTarget.Injectable });
RxRuleDefinitionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDefinitionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDefinitionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1$1.HttpClient }, { type: i1.RxCommandFactoryService }]; } });

class RxRuleElementRegistryService {
    constructor(rxGlobalCacheService, rxLogService, rxProcessDefinitionService) {
        this.rxGlobalCacheService = rxGlobalCacheService;
        this.rxLogService = rxLogService;
        this.rxProcessDefinitionService = rxProcessDefinitionService;
        this.baseServerAction = null;
        this.cellNamespaceValue = {};
        this.cellViewNamespaceValue = {};
        this.serverActionDescriptors = new Map();
        this.serverActionOwnerBundleIds = {};
        this.specialActionDescriptors = new Map();
        this.standardElementDescriptors = new Map();
        this.unresolvedServerActionDescriptors = [];
        this.serverActionOwnerBundleIds$ = this.rxGlobalCacheService
            .getBundleDescriptors()
            .pipe(map((bundleDescriptors) => {
            bundleDescriptors.unshift(...remove(bundleDescriptors, { id: RX_APPLICATION.platformBundleId }));
            return bundleDescriptors.reduce((ownerBundleIds, bundleDescriptor) => {
                bundleDescriptor.uiOptions.serverActions?.forEach((serverActionName) => {
                    if (ownerBundleIds.hasOwnProperty(serverActionName)) {
                        this.rxLogService.warning(`[Rule Element Registry] Server action ${serverActionName} is already provided by ${ownerBundleIds[serverActionName]}.`);
                    }
                    else {
                        ownerBundleIds[serverActionName] = bundleDescriptor.id;
                    }
                });
                return ownerBundleIds;
            }, this.serverActionOwnerBundleIds);
        }), shareReplay(1));
        this.unresolvedServerActionDescriptors$ = this.serverActionOwnerBundleIds$.pipe(switchMap((ownerBundleIds) => Promise.allSettled(this.unresolvedServerActionDescriptors).then((descriptors) => {
            descriptors.forEach((result) => {
                if (result.status === 'fulfilled') {
                    const descriptor = result.value;
                    if (ownerBundleIds[descriptor.actionTypeName]) {
                        this.rxLogService.debug(`[Rule Element Registry] Server action ${descriptor.actionTypeName} has a custom implementation provided by ${ownerBundleIds[descriptor.actionTypeName]}.`);
                    }
                    this.registerServerActionSync(descriptor);
                }
                else {
                    this.rxLogService.error(`[Rule Element Registry] Server action could not be registered. ${result.reason}`);
                }
            });
            return this.serverActionDescriptors;
        })), tap(() => {
            this.unresolvedServerActionDescriptors.splice(0, this.unresolvedServerActionDescriptors.length);
        }));
    }
    get cellNamespace() {
        return this.cellNamespaceValue;
    }
    get cellViewNamespace() {
        return this.cellViewNamespaceValue;
    }
    getBaseServerAction() {
        return this.baseServerAction;
    }
    getElementDisplayNameByModelType(modelType) {
        let displayName;
        if (this.isStandardAction(modelType) || this.isSpecialAction(modelType)) {
            const actionName = this.getServerActionName(modelType);
            const descriptor = this.specialActionDescriptors.get(actionName) ?? this.getServerAction(actionName) ?? this.baseServerAction;
            displayName = descriptor.displayName;
        }
        else {
            displayName = this.getStandardElement(modelType).displayName;
        }
        return displayName;
    }
    getElementServiceByModelType(modelType) {
        let service;
        if (this.isStandardAction(modelType) || this.isSpecialAction(modelType)) {
            const actionName = this.getServerActionName(modelType);
            const descriptor = this.specialActionDescriptors.get(actionName) ?? this.getServerAction(actionName) ?? this.baseServerAction;
            service = descriptor.service;
        }
        else {
            service = this.getStandardElement(modelType).service;
        }
        return service;
    }
    getServerAction(actionName) {
        return (this.specialActionDescriptors.get(actionName) ??
            this.serverActionDescriptors.get(actionName) ??
            this.baseServerAction);
    }
    getServerActionName(modelType) {
        return last(modelType.split(`${RX_RULE_DEFINITION.modelTypes.ruleActions}.`));
    }
    getServerActionNames() {
        return [...this.serverActionDescriptors.keys()];
    }
    getServerActions() {
        return [...this.serverActionDescriptors.values()];
    }
    getSpecialActions() {
        return [...this.specialActionDescriptors.values()];
    }
    getStandardElement(layoutType) {
        return this.standardElementDescriptors.get(layoutType);
    }
    getStandardElements() {
        return [...this.standardElementDescriptors.values()];
    }
    registerBaseServerAction(baseServerAction) {
        if (!this.baseServerAction) {
            this.baseServerAction = baseServerAction;
            set(this.cellNamespaceValue, RX_RULE_DEFINITION.modelTypes.ruleAction, this.baseServerAction.model);
            set(this.cellViewNamespaceValue, `${RX_RULE_DEFINITION.modelTypes.ruleAction}View`, this.baseServerAction.viewModel);
        }
    }
    registerServerAction(descriptor) {
        this.unresolvedServerActionDescriptors.push(Promise.resolve(descriptor));
    }
    registerSpecialAction(descriptor) {
        set(this.cellNamespaceValue, descriptor.actionTypeName, descriptor.model);
        set(this.cellViewNamespaceValue, `${descriptor.actionTypeName}View`, descriptor.viewModel);
        this.specialActionDescriptors.set(descriptor.actionTypeName, descriptor);
    }
    registerStandardElement(descriptor) {
        set(this.cellNamespaceValue, descriptor.modelType, descriptor.model);
        set(this.cellViewNamespaceValue, `${descriptor.modelType}View`, descriptor.viewModel);
        this.standardElementDescriptors.set(descriptor.modelType, descriptor);
    }
    isRegisteredStandardAction(modelType) {
        return modelType.startsWith(RX_RULE_DEFINITION.modelTypes.ruleAction) && !this.isSpecialAction(modelType);
    }
    isSpecialAction(modelType) {
        return modelType === RX_RULE_DEFINITION.specialActionNames.connector;
    }
    isStandardAction(modelType) {
        return this.isRegisteredStandardAction(modelType) || this.isUnregisteredStandardAction(modelType);
    }
    isUnregisteredStandardAction(modelType) {
        return modelType === RX_RULE_DEFINITION.modelTypes.ruleAction;
    }
    registerServerActionSync(descriptor) {
        const modelType = this.rxProcessDefinitionService.normalizeModelType(`${RX_RULE_DEFINITION.modelTypes.ruleActions}.${descriptor.actionTypeName}`);
        set(this.cellNamespaceValue, modelType, descriptor.model || this.baseServerAction.model);
        set(this.cellViewNamespaceValue, `${modelType}View`, this.baseServerAction.viewModel);
        this.serverActionDescriptors.set(descriptor.actionTypeName, descriptor);
    }
}
RxRuleElementRegistryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleElementRegistryService, deps: [{ token: i1.RxGlobalCacheService }, { token: i1.RxLogService }, { token: i2$1.RxProcessDefinitionService }], target: i0.ɵɵFactoryTarget.Injectable });
RxRuleElementRegistryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleElementRegistryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleElementRegistryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.RxGlobalCacheService }, { type: i1.RxLogService }, { type: i2$1.RxProcessDefinitionService }]; } });

class RxRuleDesignerDependencyResolver {
    constructor(rxBundleService, rxGlobalCacheService, rxLogService, rxRuleElementRegistryService) {
        this.rxBundleService = rxBundleService;
        this.rxGlobalCacheService = rxGlobalCacheService;
        this.rxLogService = rxLogService;
        this.rxRuleElementRegistryService = rxRuleElementRegistryService;
    }
    resolve() {
        let dependencyBundleDescriptors = [];
        return this.rxGlobalCacheService.getLicensedBundleDescriptors().pipe(map((bundleDescriptors) => bundleDescriptors.filter((bundleDescriptor) => !isEmpty(bundleDescriptor.uiOptions?.serverActions))), tap((bundleDescriptors) => (dependencyBundleDescriptors = bundleDescriptors)), map((bundleDescriptors) => bundleDescriptors.map((bundleDescriptor) => bundleDescriptor.id)), switchMap((bundleIds) => bundleIds.length ? this.rxBundleService.loadBundles(bundleIds, true) : of(bundleIds)), switchMap((bundleContexts) => forkJoin([this.rxRuleElementRegistryService.unresolvedServerActionDescriptors$]).pipe(map(() => bundleContexts))), tap((bundleContexts) => {
            const registeredServerActionTypeNames = new Set(this.rxRuleElementRegistryService.getServerActionNames());
            forEach(bundleContexts, (bundleContext) => {
                if (bundleContext.isBundleCompiled) {
                    const bundleDescriptor = find(dependencyBundleDescriptors, {
                        id: bundleContext.bundleId
                    });
                    if (bundleDescriptor) {
                        const unregisteredServerActionTypeNames = filter(bundleDescriptor.uiOptions.serverActions, (serverActionName) => !registeredServerActionTypeNames.has(serverActionName));
                        if (!isEmpty(unregisteredServerActionTypeNames)) {
                            this.rxLogService.warning(`[Rule Designer Dependency] ${bundleDescriptor.id}: Unregistered Server actions found in the manifest\n${unregisteredServerActionTypeNames}`);
                        }
                    }
                }
            });
        }), shareReplay(1));
    }
}
RxRuleDesignerDependencyResolver.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDesignerDependencyResolver, deps: [{ token: i1.RxBundleService }, { token: i1.RxGlobalCacheService }, { token: i1.RxLogService }, { token: RxRuleElementRegistryService }], target: i0.ɵɵFactoryTarget.Injectable });
RxRuleDesignerDependencyResolver.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDesignerDependencyResolver, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleDesignerDependencyResolver, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.RxBundleService }, { type: i1.RxGlobalCacheService }, { type: i1.RxLogService }, { type: RxRuleElementRegistryService }]; } });

class RxRuleTriggerEventPipe {
    transform(triggerEvent) {
        let result = '';
        if (triggerEvent) {
            const recordTriggerEvent = triggerEvent;
            const systemTriggerEvent = triggerEvent;
            const timerTriggerEvent = triggerEvent;
            if (timerTriggerEvent.timeCriteria?.resourceType === TriggerEventTimeCriteriaResourceTypes.Schedule) {
                result = RX_RULE_DEFINITION.timerTypes.schedule;
            }
            else if (timerTriggerEvent.timeCriteria?.resourceType === TriggerEventTimeCriteriaResourceTypes.Interval) {
                result = RX_RULE_DEFINITION.timerTypes.interval;
            }
            else if (recordTriggerEvent.eventTypes) {
                result = recordTriggerEvent.eventTypes.join(', ');
            }
            else if (systemTriggerEvent.eventTypeDisplayName) {
                result = systemTriggerEvent.eventTypeDisplayName;
            }
        }
        return result;
    }
}
RxRuleTriggerEventPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleTriggerEventPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
RxRuleTriggerEventPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: RxRuleTriggerEventPipe, name: "rxRuleTriggerEventPipe" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleTriggerEventPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'rxRuleTriggerEventPipe'
                }]
        }] });

class RxRuleTriggerTimeCriteriaDisplayValuePipe {
    transform(ruleTriggerTimeCriteria) {
        let displayValue;
        switch (ruleTriggerTimeCriteria.resourceType) {
            case TriggerEventTimeCriteriaResourceTypes.Interval:
                displayValue = this.buildIntervalTimeDisplayValue(ruleTriggerTimeCriteria);
                break;
            case TriggerEventTimeCriteriaResourceTypes.Schedule:
                displayValue = this.buildScheduleTimeDisplayValue(ruleTriggerTimeCriteria);
                break;
            default:
                displayValue = '';
        }
        return displayValue;
    }
    buildIntervalTimeDisplayValue(intervalTimeCriteria) {
        const displayValueParts = [];
        function buildDisplayValuePart(value, singularUnit, pluralUnit) {
            return value ? `${value} ${value > 1 ? pluralUnit : singularUnit}` : '';
        }
        displayValueParts.push(buildDisplayValuePart(intervalTimeCriteria.days, 'day', 'days'));
        displayValueParts.push(buildDisplayValuePart(intervalTimeCriteria.hours, 'hour', 'hours'));
        displayValueParts.push(buildDisplayValuePart(intervalTimeCriteria.minutes, 'minute', 'minutes'));
        return `Every ${compact(displayValueParts).join(', ')}`;
    }
    buildScheduleTimeDisplayValue(scheduleTimeCriteria) {
        const weekDayOrderIds = {
            Monday: 1,
            Tuesday: 2,
            Wednesday: 3,
            Thursday: 4,
            Friday: 5,
            Saturday: 6,
            Sunday: 7
        };
        function enumerateValues(values) {
            const trailingValue = values.length > 1 ? ` and ${values.pop()}` : '';
            return `${values.join(', ')}${trailingValue}`;
        }
        const formattedWeekDays = chain(scheduleTimeCriteria.weekDays)
            .map(toLower)
            .map(startCase)
            .sortBy((weekDay) => weekDayOrderIds[weekDay])
            .value();
        const formattedTime = sortBy(scheduleTimeCriteria.hours).map((hour) => moment(`${hour}:${scheduleTimeCriteria.minute}`, ['H:mm'], 'en').format('h:mmA'));
        const monthDaysPart = scheduleTimeCriteria.monthDays?.length
            ? `On dates ${enumerateValues(sortBy(scheduleTimeCriteria.monthDays))} of every month`
            : '';
        const weekDaysPart = scheduleTimeCriteria.weekDays?.length
            ? `${monthDaysPart ? ', and every ' : 'Every '}${enumerateValues(formattedWeekDays)}`
            : '';
        const timePart = ` at ${enumerateValues(formattedTime)}`;
        return `${monthDaysPart}${weekDaysPart}${timePart}`;
    }
}
RxRuleTriggerTimeCriteriaDisplayValuePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleTriggerTimeCriteriaDisplayValuePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
RxRuleTriggerTimeCriteriaDisplayValuePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: RxRuleTriggerTimeCriteriaDisplayValuePipe, name: "rxRuleTriggerTimeCriteriaDisplayValue" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRuleTriggerTimeCriteriaDisplayValuePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'rxRuleTriggerTimeCriteriaDisplayValue'
                }]
        }] });

class RxRulePipesModule {
}
RxRulePipesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRulePipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RxRulePipesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: RxRulePipesModule, declarations: [RxRuleTriggerEventPipe, RxRuleTriggerTimeCriteriaDisplayValuePipe], imports: [CommonModule], exports: [RxRuleTriggerEventPipe, RxRuleTriggerTimeCriteriaDisplayValuePipe] });
RxRulePipesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRulePipesModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRulePipesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [RxRuleTriggerEventPipe, RxRuleTriggerTimeCriteriaDisplayValuePipe],
                    exports: [RxRuleTriggerEventPipe, RxRuleTriggerTimeCriteriaDisplayValuePipe]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { RX_RULE_DEFINITION, RxRuleDataDictionaryService, RxRuleDefinitionCacheService, RxRuleDefinitionDataPageService, RxRuleDefinitionService, RxRuleDesignerDependencyResolver, RxRuleElementRegistryService, RxRulePipesModule, RxRuleTriggerEventPipe, RxRuleTriggerTimeCriteriaDisplayValuePipe, TriggerEventResourceTypes, TriggerEventTimeCriteriaResourceTypes, renameRuleDefinitionCommand, revertRuleCustomizationResourceType };
